import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Mail, Users as UsersIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import FormModalLayout from '../components/Common/FormModalLayout';
import Can from '../components/Common/Can';
import { getAppHomePath } from '../constants/navigation';
import { P } from '../constants/permissions';
import { useTenant } from '../contexts/TenantContext';
import { isConsultoriaTenant } from '../utils/tenantProfile';
import { authService, identityService, institutionService } from '../services/api';
import { fetchUserSeats } from '../services/subscriptionService';
import { fetchAllPages } from '../utils/apiPagination';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../i18n/userFacingError';
import QueryErrorBanner from '../components/Common/QueryErrorBanner';
import TruncationNotice from '../components/Common/TruncationNotice';

const ROLE_LABELS_ORGAO = {
  tenant_admin: 'Admin do órgão',
  gestor_ambiental: 'Gestor ambiental',
  analista: 'Analista',
  consulta: 'Consulta',
  denuncias: 'Denúncias',
  fiscal: 'Fiscal',
  empresa: 'Empresa',
  empreendimento: 'Empreendimento',
  requerente: 'Requerente (pessoa)',
  consultor: 'Consultor',
  coordenador: 'Coordenador',
};

const ROLE_LABELS_CONSULTORIA = {
  ...ROLE_LABELS_ORGAO,
  tenant_admin: 'Admin da consultoria',
  gestor_ambiental: 'Gestor',
  empresa: 'Cliente',
  empreendimento: 'Empreendimento do cliente',
};

const ROLE_OPTIONS = [
  'tenant_admin',
  'gestor_ambiental',
  'analista',
  'consulta',
  'denuncias',
  'fiscal',
  'empresa',
  'empreendimento',
  'requerente',
  'consultor',
  'coordenador',
];

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.users || payload?.data || [];
}

const STATUS_LABEL = {
  active: 'Ativo',
  inactive: 'Inativo',
  suspended: 'Suspenso',
  pending: 'Pendente',
};

function isPendingActivation(user) {
  return String(user?.status || '').toLowerCase() === 'pending';
}

const Users = () => {
  const { t } = useTranslation('users');
  const { tenant } = useTenant();
  const ROLE_LABELS = isConsultoriaTenant(tenant) ? ROLE_LABELS_CONSULTORIA : ROLE_LABELS_ORGAO;
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [setorFilter, setSetorFilter] = useState('');
  const [viewUser, setViewUser] = useState(null);
  const [roleUser, setRoleUser] = useState(null);
  const [linkUser, setLinkUser] = useState(null);
  const [linkSetorId, setLinkSetorId] = useState('');
  const [linkCargoId, setLinkCargoId] = useState('');
  const [linkRouting, setLinkRouting] = useState(true);
  const [newRole, setNewRole] = useState('consulta');

  const resendActivation = useMutation(
    (userId) => authService.resendActivation(userId),
    {
      onSuccess: () => toast.success('Link de ativação reenviado por e-mail.'),
      onError: (e) => toast.error(userFacingError(e)),
    }
  );

  const { data, isLoading, error } = useQuery(['auth-users'], () =>
    authService.listUsers({ limit: 1000, skip: 0 }).then((r) => r.data)
  );
  const users = unwrap(data);
  const { data: seats } = useQuery(['auth-user-seats'], fetchUserSeats, { retry: false });
  const { data: setores = [] } = useQuery(['users-setores'], () =>
    fetchAllPages((params) => institutionService.getSetores(params))
  );
  const { data: cargosRaw } = useQuery(['users-cargos'], () =>
    institutionService.cargoCrud.getList({ limit: 500 })
  );
  const cargos = Array.isArray(cargosRaw) ? cargosRaw : [];
  const { data: servidores = [] } = useQuery(['users-servidores'], () =>
    fetchAllPages((params) => institutionService.getServidores(params))
  );

  const setorById = useMemo(
    () => Object.fromEntries((setores || []).map((s) => [String(s.id), s])),
    [setores]
  );
  const servidorByPerson = useMemo(() => {
    const map = {};
    for (const srv of servidores || []) {
      if (srv?.person_id) map[String(srv.person_id)] = srv;
    }
    return map;
  }, [servidores]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const roles = u.roles || [];
      const matchRole = !roleFilter || roles.includes(roleFilter);
      const hay = `${u.username || ''} ${u.email || ''}`.toLowerCase();
      const srv = u.person_id ? servidorByPerson[String(u.person_id)] : null;
      const matchSetor = !setorFilter || (srv && String(srv.setor_id) === String(setorFilter));
      return matchRole && matchSetor && (!q || hay.includes(q));
    });
  }, [users, search, roleFilter, setorFilter, servidorByPerson]);

  const setorLabel = (user) => {
    const srv = user.person_id ? servidorByPerson[String(user.person_id)] : null;
    if (!srv) return '—';
    return setorById[String(srv.setor_id)]?.name || srv.setor_id || '—';
  };

  const assignRole = useMutation(
    ({ userId, roleName }) => authService.assignUserRole(userId, roleName),
    {
      onSuccess: () => {
        toast.success('Papel atualizado');
        queryClient.invalidateQueries(['auth-users']);
        queryClient.invalidateQueries(['role-audit']);
        setRoleUser(null);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const removeRole = useMutation(
    ({ userId, roleName }) => authService.removeUserRole(userId, roleName),
    {
      onSuccess: (_data, vars) => {
        toast.success('Papel removido');
        queryClient.invalidateQueries(['auth-users']);
        queryClient.invalidateQueries(['role-audit']);
        setViewUser((current) =>
          current && current.id === vars.userId
            ? { ...current, roles: (current.roles || []).filter((r) => r !== vars.roleName) }
            : current
        );
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const linkSetor = useMutation(
    async ({ user, setorId, cargoId, receiveRouting }) => {
      let personId = user.person_id;
      if (!personId) {
        const created = await identityService.createPerson({
          name: user.username || user.email || 'Usuário',
          email: user.email || undefined,
        });
        personId = created.data?.id;
        if (!personId) throw new Error('Não foi possível criar a pessoa');
        await authService.updateUserEntity(user.id, { person_id: personId });
      }
      const existing = servidorByPerson[String(personId)];
      const payload = {
        setor_id: setorId,
        cargo_id: cargoId,
        receive_routing: receiveRouting,
      };
      if (existing?.id) {
        await institutionService.updateServidor(existing.id, payload);
      } else {
        await institutionService.createServidor({
          person_id: personId,
          perfil: 2,
          ...payload,
        });
      }
    },
    {
      onSuccess: () => {
        toast.success('Usuário vinculado ao setor');
        queryClient.invalidateQueries(['auth-users']);
        queryClient.invalidateQueries(['users-servidores']);
        queryClient.invalidateQueries(['me-servidor']);
        setLinkUser(null);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const { data: auditRaw } = useQuery(
    ['role-audit', viewUser?.id],
    () => authService.getUserRoleAudit(viewUser.id).then((r) => r.data),
    { enabled: !!viewUser?.id }
  );
  const audit = Array.isArray(auditRaw) ? auditRaw : auditRaw?.items || [];

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t('title')}
          description="Contas do tenant e papéis JWT efetivos."
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Usuários' },
          ]}
        />

        {seats && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ${
              seats.blocked ? 'bg-amber-50 text-amber-900' : 'bg-gray-50 text-gray-700'
            }`}
          >
            <strong>{seats.used}</strong>
            {seats.max_users != null ? ` de ${seats.max_users} usuários do plano em uso.` : ' usuários internos ativos.'}
            {seats.blocked
              ? ' O limite foi atingido: aumente a faixa de usuários na área de assinatura ou desative um usuário antes de criar outro acesso.'
              : ' Requerentes do portal não ocupam vaga do plano.'}
          </div>
        )}

        <div className="data-list-card">
          <div className="grid grid-cols-1 gap-4 border-b border-gray-100 px-5 py-4 md:grid-cols-4">
            <div className="form-group mb-0">
              <label className="form-label">Papel</label>
              <select className="form-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">Todos</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role] || role}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0">
              <label className="form-label">Setor</label>
              <select className="form-select" value={setorFilter} onChange={(e) => setSetorFilter(e.target.value)}>
                <option value="">Todos</option>
                {setores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0 md:col-span-2">
              <label className="form-label">Buscar</label>
              <input
                className="form-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome ou e-mail"
              />
            </div>
          </div>
          {error ? (
            <div className="px-6 py-4">
              <QueryErrorBanner error={error} />
            </div>
          ) : isLoading ? (
            <p className="px-6 py-8 text-gray-500">Carregando...</p>
          ) : (
            <>
            <div className="px-6 pt-3">
              <TruncationNotice loaded={users.length} requestedLimit={1000} />
            </div>
            <div className="overflow-x-auto">
            <table className="table min-w-[72rem]">
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>E-mail</th>
                  <th>Papéis</th>
                  <th>Setor</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => (
                  <tr key={user.id} className="group">
                    <td>
                      <div className="font-medium text-gray-900">{user.username}</div>
                      <div className="max-w-[12rem] truncate text-xs text-gray-500" title={user.id}>{user.id}</div>
                    </td>
                    <td className="text-sm">
                      <Mail className="mr-1 inline h-4 w-4 text-gray-400" />
                      {user.email}
                    </td>
                    <td className="text-sm">{(user.roles || []).join(', ') || '—'}</td>
                    <td className="text-sm">{setorLabel(user)}</td>
                    <td>
                      <span className={`badge ${isPendingActivation(user) ? 'badge-warning' : user.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {STATUS_LABEL[String(user.status || '').toLowerCase()] || (user.is_active ? 'Ativo' : 'Inativo')}
                      </span>
                    </td>
                    <td className="sticky right-0 z-10 whitespace-nowrap bg-white text-right shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.18)] group-hover:bg-gray-50">
                      <Can permission={P.USERS_MANAGE}>
                        <div className="flex flex-nowrap justify-end gap-2">
                        {isPendingActivation(user) && (
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            title="Reenviar o link de ativação por e-mail"
                            disabled={resendActivation.isLoading && resendActivation.variables === user.id}
                            onClick={() => resendActivation.mutate(user.id)}
                          >
                            {resendActivation.isLoading && resendActivation.variables === user.id ? 'Enviando…' : 'Reenviar ativação'}
                          </button>
                        )}
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => setViewUser(user)}>
                          Ver
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setNewRole('');
                            setRoleUser(user);
                          }}
                        >
                          Papel
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => {
                            const srv = user.person_id ? servidorByPerson[String(user.person_id)] : null;
                            setLinkSetorId(srv?.setor_id || '');
                            setLinkCargoId(srv?.cargo_id || cargos[0]?.id || '');
                            setLinkRouting(srv?.receive_routing !== false);
                            setLinkUser(user);
                          }}
                        >
                          Setor
                        </button>
                        </div>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </>
          )}
        </div>
      </div>

      {viewUser && (
        <FormModalLayout
          title={t('detailsTitle')}
          subtitle="Dados retornados pelo auth-service."
          headerIcon={UsersIcon}
          onClose={() => setViewUser(null)}
          footer={
            <button type="button" className="btn btn-outline" onClick={() => setViewUser(null)}>
              Fechar
            </button>
          }
        >
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between border-b py-2">
              <dt>Username</dt>
              <dd>{viewUser.username}</dd>
            </div>
            <div className="flex justify-between border-b py-2">
              <dt>E-mail</dt>
              <dd>{viewUser.email}</dd>
            </div>
            <div className="flex justify-between border-b py-2">
              <dt>Setor</dt>
              <dd>{setorLabel(viewUser)}</dd>
            </div>
            <div className="flex justify-between py-2">
              <dt>Papéis</dt>
              <dd className="flex flex-wrap gap-1">
                {(viewUser.roles || []).length ? (
                  viewUser.roles.map((role) => (
                    <span key={role} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5">
                      {ROLE_LABELS[role] || role}
                      <Can permission={P.USERS_MANAGE}>
                        <button
                          type="button"
                          className="text-red-600"
                          onClick={() => removeRole.mutate({ userId: viewUser.id, roleName: role })}
                        >
                          ×
                        </button>
                      </Can>
                    </span>
                  ))
                ) : (
                  '—'
                )}
              </dd>
            </div>
          </dl>
          <h4 className="mt-4 text-sm font-semibold">Auditoria de papéis</h4>
          {audit.length === 0 ? (
            <p className="mt-2 text-xs text-gray-500">Nenhuma alteração registrada.</p>
          ) : (
            <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs">
              {audit.map((row) => (
                <li key={row.id} className="border-b border-gray-100 py-1">
                  {row.action === 'assign' ? 'Atribuiu' : 'Removeu'} <strong>{row.role_name}</strong>
                  {row.created_at ? ` · ${String(row.created_at).replace('T', ' ').slice(0, 16)}` : ''}
                </li>
              ))}
            </ul>
          )}
        </FormModalLayout>
      )}

      {linkUser && (
        <FormModalLayout
          title={`Setor de ${linkUser.username}`}
          subtitle="Vincula a pessoa do login a um servidor e setor do órgão."
          onClose={() => setLinkUser(null)}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!linkSetorId || !linkCargoId || linkSetor.isLoading}
              onClick={() =>
                linkSetor.mutate({
                  user: linkUser,
                  setorId: linkSetorId,
                  cargoId: linkCargoId,
                  receiveRouting: linkRouting,
                })
              }
            >
              Vincular
            </button>
          }
        >
          <div className="space-y-4">
            <div className="form-group mb-0">
              <label className="form-label">Setor</label>
              <select className="form-select" value={linkSetorId} onChange={(e) => setLinkSetorId(e.target.value)}>
                <option value="">Selecione</option>
                {setores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name || s.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group mb-0">
              <label className="form-label">Cargo</label>
              <select className="form-select" value={linkCargoId} onChange={(e) => setLinkCargoId(e.target.value)}>
                <option value="">Selecione</option>
                {cargos.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={linkRouting}
                onChange={(e) => setLinkRouting(e.target.checked)}
              />
              Receber tramitação
            </label>
          </div>
        </FormModalLayout>
      )}

      {roleUser && (
        <FormModalLayout
          title={`Papel de ${roleUser.username}`}
          onClose={() => {
            setNewRole('');
            setRoleUser(null);
          }}
          footer={
            <button
              type="button"
              className="btn btn-primary"
              disabled={!newRole || assignRole.isLoading}
              onClick={() => assignRole.mutate({ userId: roleUser.id, roleName: newRole })}
            >
              Definir papel
            </button>
          }
        >
          <label className="form-label">Novo papel</label>
          <select className="form-select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
            <option value="">Selecione o papel</option>
            {ROLE_OPTIONS.filter((role) => !(roleUser.roles || []).includes(role)).map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role] || role}
              </option>
            ))}
          </select>
        </FormModalLayout>
      )}
    </FormPageShell>
  );
};

export default Users;
